import { Badge, Button, SelectProps, SpaceBetween } from '@amzn/awsui-components-console';
import React, { useEffect, useMemo, useState } from 'react';
import { Column, ColumnContainer, LabeledContent, LabeledContentSkeleton, transformTo } from '../../helpers';
import { ActivityType } from '../../../constants/hammerstoneConstants';
import PAGES from '../../../nav/Pages';
import { useConditionalEffect, objToOptions } from 'src/commons/';
import Activity, { ActivityContainerProps } from '../../../interfaces/activityInterfaces';

import { fetchInstanceById, fetchPipelineInfo, fetchPipelineNames } from '../../../data/api/fetchFromAPI';
import store, { useS3CredentialType, useFormField, useGroupName, usePipeline, usePipelines } from 'src/data/redux';
import DjsActivityContent from './DjsActivityContent';
import Content, { Rules } from 'src/components/helpers/content/';
import { DisplayMode } from 'src/components/helpers/content/contentInterfaces';
import { LastExecutionDate, LastExecutionStatus, useActivityWithStatus } from '../LastActivityExecution';
import keys from 'src/constants/hammerstoneConstantKeys';
import InternalLink from 'src/components/helpers/InternalLink';

interface iAboutContainer extends ActivityContainerProps {
	activityTypeOptions?: SelectProps.Options;
}

function ViewAboutContainer(props: iAboutContainer) {
	const { activityId } = props;
	const groupName = useGroupName();
	const activityWithStatus = useActivityWithStatus(activityId);

	const contentProps = {
		resourceId: activityId,
		resourceType: 'activity',
		mode: DisplayMode.View,
	} as const;

	const PENDING_STATUSES: string[] = [keys.StatusCode.EDIT_PENDING, keys.StatusCode.NEW];

	return (
		<ColumnContainer header={props.header} columns={3}>
			<Column>
				<Content.Text
					{...contentProps}
					label="Activity status"
					path="activityStatusCode"
					data-testid="activity-status"
					viewTransform={transformTo.statusIndicator}
				/>
				<Content.Select
					{...contentProps}
					label="Type"
					path="activityType"
					options={props.activityTypeOptions}
					infoHelpPanel="ActivityType"
				/>
				<Content.Number
					{...contentProps}
					label="Revision"
					path="revision"
					viewTransform={(revision) => (
						<SpaceBetween size="s" direction="horizontal">
							<Badge
								color={
									PENDING_STATUSES.includes(activityWithStatus?.activityStatusCode) ? 'blue' : 'green'
								}
							>
								{revision}
							</Badge>
							<InternalLink href={PAGES.ACTIVITY_HISTORY.path} pathParams={{ activityId }} groupName>
								History
							</InternalLink>
						</SpaceBetween>
					)}
					missingText="activate activity to create revision"
				/>
				<Content.Textarea
					{...contentProps}
					label="Revision comment"
					path="comment"
					missingText="no revision comment"
				/>
				<DjsActivityContent activityId={contentProps.resourceId} mode={contentProps.mode} />
			</Column>
			<Column>
				<Content.Text
					{...contentProps}
					label="Pipeline"
					path="activityNamespace"
					viewTransform={(pipelineName: any) => (
						<InternalLink groupName href={PAGES.VIEW_PIPELINE.path} pathParams={{ pipelineName }}>
							{pipelineName}
						</InternalLink>
					)}
				/>
				<Content.Text
					{...contentProps}
					label="Last execution instance ID"
					path="activityInstanceId"
					missingText="no activity instance ID found"
					viewTransform={(instanceId) =>
						instanceId ? (
							<InternalLink
								href={PAGES.VIEW_INSTANCE.path}
								pathParams={{ instanceId, activityId }}
								groupName
							>
								{instanceId}
							</InternalLink>
						) : null
					}
				/>
				<LabeledContent label="Last execution status" missingText={null} data-testid="last-execution-status">
					<LastExecutionStatus activityId={props.activityId} />{' '}
					<Button
						variant="inline-icon"
						iconName="refresh"
						onClick={() =>
							fetchInstanceById(
								{
									groupName,
									activityId: parseInt(props.activityId),
									instanceId: activityWithStatus?.activityInstanceId,
								},
								true
							)
						}
					/>
				</LabeledContent>
				<LabeledContent label="Last execution date" missingText={null}>
					<LastExecutionDate
						lastExecutionDate={activityWithStatus?.lastExecutionDate}
						activityStatusCode={activityWithStatus?.activityStatusCode}
					/>
				</LabeledContent>
				<Content.Text
					{...contentProps}
					label="Confidence file"
					path="confidenceFileLocation"
					infoHelpPanel="ConfidenceFile"
				/>
			</Column>
			<Column>
				<Content.Datetime {...contentProps} label="Create date" path="createDate" />
				<Content.Text
					{...contentProps}
					label="Created by"
					path="createdBy"
					viewTransform={transformTo.userProfile}
				/>
				<Content.Datetime {...contentProps} label="Update date" path="updateDate" />
				<Content.Text
					{...contentProps}
					label="Updated by"
					path="updatedBy"
					viewTransform={transformTo.userProfile}
				/>
			</Column>
		</ColumnContainer>
	);
}

function EditAboutContainer(props: iAboutContainer) {
	const groupName = useGroupName();

	// Will only be edited during Activity creation, otherwise is view-only
	const { data } = store.getState();
	const contentProps = { mode: DisplayMode.Edit, resourceType: 'activity', resourceId: '' } as const;
	const { data: pipelines } = usePipelines(groupName);
	const pipelineName: string = useFormField<Activity>('activityNamespace');
	const { data: pipeline } = usePipeline({ groupName, pipelineName });
	const activityNames = pipeline
		? pipeline.activityInfoList
				.filter(({ activityId }) => activityId.toString() !== props.activityId)
				.map(({ activityName }) => activityName)
		: [];

	const isIamPipeline = useS3CredentialType({ pipelineName, groupName }) !== 'ODIN';

	useConditionalEffect(() => {
		fetchPipelineNames({ groupName });
	}, [groupName]);

	useConditionalEffect(() => {
		fetchPipelineInfo({ groupName, pipelineName });
	}, [pipelineName, groupName]);

	const [pipelineOptions, setPipelineOptions] = useState<SelectProps.Option[]>(undefined);

	useEffect(() => {
		// This Effect hook updates which pipeline options are disabled due to their usage of Odin credentials
		if (pipelines?.length) {
			setPipelineOptions(
				pipelines.map((p) => {
					return {
						value: p,
						label: p,
						disabled:
							data.pipeline.data[p]?.s3CredentialType === 'ODIN' ||
							(p === pipelineName && pipeline?.s3CredentialType === 'ODIN'),
					};
				})
			);
		}
	}, [pipelineName, pipeline, pipelines]);

	return (
		<ColumnContainer header={props.header} columns={3}>
			<Content.Text
				{...contentProps}
				label="Activity name"
				path="activityName"
				rules={(l) => ({
					required: Rules.required(l),
					minLength: Rules.minLength(1, l),
					maxLength: Rules.maxLength(128, l),
					pattern: Rules.pattern.lettersNumbersHyphenUnderscore(l),
					validate: {
						uniqueName: (activityName) =>
							!activityNames.includes(activityName) ||
							`An activity named "${activityName}" already exists in pipeline ${pipelineName}, please choose a unique name.`,
					},
				})}
				placeholder="New activity name..."
			/>
			<Content.Select
				{...contentProps}
				path="activityNamespace"
				label="Pipeline"
				options={pipelineOptions}
				filteringType="auto"
				rules={(l) => ({
					required: Rules.required(l),
					validate: {
						iamPipeline: (pipelineName) =>
							// Pulls the current version of the Redux state to determine whether this activity belongs to a deprecated Odin pipeline
							isIamPipeline ||
							`Pipeline "${pipelineName}" uses Odin credentials. Since Odins are being deprecated, activity creation is forbidden in Odin pipelines. Please select another pipeline or migrate this pipeline to IAM.`,
					},
				})}
			/>
			<Content.Select
				{...contentProps}
				path="activityType"
				label="Activity type"
				options={props.activityTypeOptions}
				infoHelpPanel="ActivityType"
				rules={(l) => ({ required: Rules.required(l) })}
			/>
		</ColumnContainer>
	);
}

/**
 * The typical ViewAboutContainer shows several unmodifiable fields that are specific to only the current
 * activity configuration, and not to its revision history/diff. In order to prevent customer confusion while
 * maintaining a seemingly consistent user interface, this Container will have a dedicated diff layout
 * with relevant metadata fields including activity name, revision, comment, update date, and updated by
 */
function DiffAboutContainer(props: iAboutContainer) {
	const contentProps = {
		resourceId: props.activityId,
		diffResourceId: props.diffActivityId,
		resourceType: 'activity',
		mode: DisplayMode.Diff,
	} as const;

	return (
		<ColumnContainer header={props.header} columns={3}>
			<Content.Text {...contentProps} label="Activity name" path="activityName" />
			<Content.Text {...contentProps} label="Revision" path="revision" />
			<Content.Text {...contentProps} label="Comment" path="comment" missingText="no revision comment" />
			<Content.Datetime {...contentProps} label="Update date" path="updateDate" />
			<Content.Text {...contentProps} label="Updated by" path="updatedBy" />
		</ColumnContainer>
	);
}

/** A container with overview information about a particular Activity */
export default function AboutContainer(props: iAboutContainer) {
	// Memoized at top level to avoid unnecessary computations
	const ActivityTypeOptions = useMemo(() => objToOptions(ActivityType), []);

	switch (props.mode) {
		case DisplayMode.View:
			return <ViewAboutContainer {...props} activityTypeOptions={ActivityTypeOptions} />;
		case DisplayMode.Edit:
			if (props.activityId) {
				// No "about" container for editing an activity, only creating
				return null;
			} else {
				return <EditAboutContainer {...props} activityTypeOptions={ActivityTypeOptions} />;
			}
		case DisplayMode.Loading:
			return (
				<ColumnContainer header={props.header} columns={3}>
					<LabeledContentSkeleton label="Name" />
					<LabeledContentSkeleton label="Pipeline" />
					<LabeledContentSkeleton label="Type" />
				</ColumnContainer>
			);
		case DisplayMode.Diff:
			return <DiffAboutContainer {...props} />;
	}
}
